import { Attachment, Chat } from "@/types/chat"
import { CollectionRef } from "@/types/collection"
import { Timestamp } from "@/types/date"
import { InspirationRef, PlaceRef } from "@/types/entity"
import {
  DefaultInspirationPerPage,
  DetailedInspiration,
  DetailedInspirationItem,
  InspirationAllStatus,
  InspirationContentSource,
  InspirationInitialState,
  InspirationItemAddBlock,
  InspirationItemAddBlockAfter,
  InspirationItemAddBlockBefore,
  InspirationItemBlockAudio,
  InspirationItemBlockGuide,
  InspirationItemBlockLink,
  InspirationItemSummary,
  InspirationItemUpdateBlock,
  InspirationLayout,
  InspirationPaymentFree,
  InspirationPhotoDisplay,
  InspirationPublishedStatus,
  InspirationStatus,
  InspirationSummary,
  InspirationWhere,
} from "@/types/inspiration"
import { Location, PlaceAudio, PlacePhoto } from "@/types/place"
import { MapViewport } from "@/types/util"

import { BaseApi, BaseApiRequestConfig } from "@/lib/base-api"
import { present } from "@/lib/utils/presence"
import { DefaultInspirationItemFetchSize } from "./inspiration"
import { PagingMeta } from "./paging"

export interface FetchInspirationsArgs {
  status?: InspirationStatus
  page?: number
  perPage?: number
  searchTerm?: string
  locationEntityRef?: PlaceRef
}

export interface FetchInspirationsStaticListArgs {
  type?: "home"
}

export interface FetchPublicUserInspirationsArgs {
  handle: string
  page?: number
  perPage?: number
}

export type InspirationBotGenerateOption = "new" | "less" | "more" | "rewrite"

export interface CommissionData {
  id: number
  vendor: string
  booking_date: Timestamp
  status: string
  commission: number
  commission_status?: string
  total_amount: string
  start_date: Timestamp
  end_date: Timestamp
  source_name: string
  source_type: string
  projected_payout_date: Timestamp
  product_name: string
  destination_names?: string[]
}

export interface CommissionResponse {
  data: CommissionData[]
  meta: PagingMeta
}

export interface CommissionMetrics {
  booked_count: number
  completed_count: number
  commission_earnings: number
  unpaid_confirmed_commission_earnings: number
}

export class InspirationApi extends BaseApi {
  // This fetch from the DB (not inspiration index). Should be use for /create, /admin, /profile page
  async fetchUserInspirations(
    {
      status = InspirationAllStatus,
      page = 1,
      perPage = DefaultInspirationPerPage,
      searchTerm,
      locationEntityRef,
    }: FetchInspirationsArgs,
    customConfig?: BaseApiRequestConfig
  ) {
    let path = `api/inspirations/current-user?status=${status}&page=${page}&per_page=${perPage}`
    if (present(searchTerm)) {
      path = `${path}&search=${searchTerm}`
    }
    if (locationEntityRef) {
      path = `${path}&location_entity_ref=${locationEntityRef}`
    }
    return await this.get<{ data: InspirationSummary[]; meta: PagingMeta }>(
      path,
      customConfig
    )
  }

  async fetchPublicUserInspirations(
    {
      handle,
      page = 1,
      perPage = DefaultInspirationPerPage,
    }: FetchPublicUserInspirationsArgs,
    customConfig?: BaseApiRequestConfig
  ) {
    const path = `api/inspirations?handle=${handle}&status=${InspirationPublishedStatus}&payment=${InspirationPaymentFree}&page=${page}&per_page=${perPage}`
    return await this.get<{ data: InspirationSummary[]; meta: PagingMeta }>(
      path,
      customConfig
    )
  }

  async fetchUserInspirationsCount(
    status: InspirationStatus = InspirationPublishedStatus
  ) {
    // We use the db_count instead of index count to get more accurate count for current user as you might have just created an inspiration
    // that might not have been indexed yet.
    const path = `api/inspirations/count?status=${status}&owned_only=true&use_db=true`
    const ret = await this.get<{
      count: number
    }>(path)
    return ret.count
  }

  async fetchPublicUserInspirationsCount(handle: string) {
    const path = `api/inspirations/count?status=${InspirationPublishedStatus}&payment=${InspirationPaymentFree}&handle=${handle}`
    const ret = await this.get<{
      count: number
    }>(path)
    return ret.count
  }

  async fetchInspirationsStaticList({ type }: FetchInspirationsStaticListArgs) {
    let path = "api/inspirations/by_ids"
    if (type) {
      path = `${path}?type=${type}`
    }
    return (await this.get<{ inspirations: InspirationSummary[] }>(path))
      .inspirations
  }

  async fetchInspiration(
    inspirationRef: InspirationRef,
    batchSize?: number,
    customConfig?: BaseApiRequestConfig
  ) {
    let path = `api/inspirations/${inspirationRef}`
    if (batchSize) {
      path = `${path}?batch_size=${batchSize}`
    }
    return await this.get<DetailedInspiration>(path, customConfig)
  }

  async fetchInspirationItems(
    inspirationRef: InspirationRef,
    currPage: number,
    perPage = DefaultInspirationItemFetchSize,
    customConfig?: BaseApiRequestConfig
  ) {
    const path = `api/inspirations/${inspirationRef}/items?page=${currPage}&per_page=${perPage}`
    return await this.get<{
      data: DetailedInspirationItem[]
      meta: PagingMeta
    }>(path, customConfig)
  }

  async fetchInspirationItemsSummary(
    inspirationRef: InspirationRef,
    currPage: number,
    perPage = DefaultInspirationItemFetchSize,
    customConfig?: BaseApiRequestConfig
  ) {
    const path = `api/inspirations/${inspirationRef}/items_summary?page=${currPage}&per_page=${perPage}`
    return await this.get<{
      data: InspirationItemSummary[]
      meta: PagingMeta
    }>(path, customConfig)
  }

  async fetchInspirationItem(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    customConfig?: BaseApiRequestConfig
  ) {
    return await this.get<{ inspiration_item: DetailedInspirationItem }>(
      `api/inspirations/${inspirationRef}/items/${inspirationItemId}`,
      customConfig
    )
  }

  async generateInspiration(
    chatId: number,
    title: string | undefined,
    type: "list" | "itinerary" = "itinerary",
    deactivateChat = false,
    attachment?: Attachment
  ) {
    const endpoint = `api/inspirations/generate_inspiration/${chatId}`

    const data: {
      title?: string
      type: "list" | "itinerary"
      deactivate_chat: boolean
      source?: Attachment
    } = {
      title: title,
      deactivate_chat: deactivateChat,
      type: type,
      source: attachment,
    }

    return await this.post<DetailedInspiration>(endpoint, data)
  }

  async generateInspirationForOrganization(
    orgid: string,
    chatId: number,
    title: string | undefined,
    type: "list" | "itinerary" = "itinerary",
    deactivateChat = false,
    attachment?: Attachment,
    customerName?: string
  ) {
    const endpoint = `api/organizations/${orgid}/inspirations/generate_inspiration/${chatId}`

    const data: {
      title?: string
      type: "list" | "itinerary"
      deactivate_chat: boolean
      source?: Attachment
      customer_name?: string
    } = {
      title: title,
      deactivate_chat: deactivateChat,
      type: type,
      source: attachment,
      customer_name: customerName,
    }

    return await this.post<DetailedInspiration>(endpoint, data)
  }

  async generateInspirationFromCollection(
    collectionRef: CollectionRef,
    title: string | undefined
  ) {
    const data = {
      title: title,
    }
    return await this.post<DetailedInspiration>(
      `api/inspirations/generate_inspiration_from_collection/${collectionRef}`,
      data
    )
  }

  async unpublishInspiration(inspirationRef: InspirationRef) {
    return await this.post<InspirationSummary>(
      `api/inspirations/${inspirationRef}/un_publish`
    )
  }

  async publishInspiration(
    inspirationRef: InspirationRef,
    publicGuide: boolean,
    unlisted = false,
    silent = false
  ) {
    const params = new URLSearchParams({
      public: String(publicGuide),
      unlisted: String(unlisted),
    })
    if (silent) params.set("silent", "true")
    return await this.post<InspirationSummary>(
      `api/inspirations/${inspirationRef}/publish?${params.toString()}`
    )
  }

  async unlockedInspiration(inspirationRef: InspirationRef) {
    return await this.post<InspirationSummary>(
      `api/inspirations/${inspirationRef}/un_locked`
    )
  }

  async createInspiration(
    numOfDays?: string,
    title?: string,
    locations?: Location[],
    layout?: InspirationLayout,
    initialState?: InspirationInitialState
  ) {
    const endpoint = `api/inspirations`

    const payload = {
      num_of_days: numOfDays,
      title,
      locations: locations?.map((location) => location.entity_ref) ?? [],
      layout,
      initial_state: initialState,
    }

    return await this.post<DetailedInspiration>(endpoint, payload)
  }

  async createInspirationForOrganization(
    orgid: string,
    numOfDays?: string,
    title?: string,
    locations?: Location[],
    layout?: InspirationLayout,
    initialState?: InspirationInitialState,
    customerName?: string
  ) {
    const endpoint = `api/organizations/${orgid}/inspirations`

    const payload = {
      num_of_days: numOfDays,
      title,
      locations: locations?.map((location) => location.entity_ref) ?? [],
      layout,
      initial_state: initialState,
      customer_name: customerName,
    }

    return await this.post<DetailedInspiration>(endpoint, payload)
  }

  async duplicateInspiration(inspirationRef: InspirationRef) {
    const endpoint = `api/inspirations/duplicate`
    return await this.post<DetailedInspiration>(endpoint, {
      inspiration_ref: inspirationRef,
    })
  }

  async saveInspiration({
    inspirationRef,
    where,
    editText,
    template,
    photos,
    displayMode,
    layout,
    contentSource,
    latitude,
    longitude,
  }: {
    inspirationRef: InspirationRef
    where: string
    editText: string
    dayIndex?: number
    template?: string
    photos?: PlacePhoto[]
    displayMode?: InspirationPhotoDisplay
    layout?: InspirationLayout
    contentSource?: InspirationContentSource
    latitude?: number
    longitude?: number
  }) {
    return await this.put<{
      inspiration: InspirationSummary
      items: DetailedInspirationItem[]
    }>(`api/inspirations/${inspirationRef}`, {
      where: where,
      edit_text: editText,
      day_index: 0, // Obsolete - to be removed later when we changed the api
      template: template,
      photo_urls: photos,
      display_mode: displayMode,
      layout: layout,
      content_source: contentSource,
      latitude: latitude,
      longitude: longitude,
    })
  }

  async saveInspirationItem({
    inspirationRef,
    inspirationItemId,
    where,
    editText,
    photos,
    displayMode,
    contentSource,
    latitude,
    longitude,
    remainderText,
  }: {
    inspirationRef: InspirationRef
    inspirationItemId: number
    where: string
    editText: string
    photos?: PlacePhoto[]
    displayMode?: InspirationPhotoDisplay
    contentSource?: InspirationContentSource
    latitude?: number
    longitude?: number
    remainderText?: string
  }) {
    const affectedInspirationItems = (
      await this.put<{
        inspiration_items: DetailedInspirationItem[]
      }>(`api/inspirations/${inspirationRef}/items/${inspirationItemId}`, {
        where: where,
        edit_text: editText,
        photo_urls: photos,
        display_mode: displayMode,
        content_source: contentSource,
        latitude: latitude,
        longitude: longitude,
        remainder_text: remainderText,
      })
    ).inspiration_items
    return affectedInspirationItems
  }

  async deleteInspiration(inspirationRef: InspirationRef) {
    return await this.delete<InspirationSummary>(
      `api/inspirations/${inspirationRef}`
    )
  }

  async generateChatTripAndItinerary(
    inspirationRef: InspirationRef,
    generateItinerary = true
  ) {
    let url = `api/inspirations/${inspirationRef}/generate_itinerary`
    if (!generateItinerary) {
      url += "?generate_itinerary=0"
    }
    return (await this.post<{ chat: Chat }>(url)).chat
  }

  async fetchInspirationsForPlace(
    placeRef: PlaceRef,
    config?: BaseApiRequestConfig & { limit?: number }
  ) {
    const { limit, ...requestConfig } = config ?? {}
    return (
      await this.get<{ inspirations: InspirationSummary[] }>(
        `api/inspirations/fetch/place/${placeRef}`,
        {
          ...requestConfig,
          params: { ...requestConfig.params, ...(limit ? { limit } : {}) },
        }
      )
    ).inspirations
  }

  async fetchRandomInspirations(count: number, config?: BaseApiRequestConfig) {
    count ||= 3
    return (
      await this.get<{ inspirations: InspirationSummary[] }>(
        `api/inspirations/fetch_random/${count}`,
        config
      )
    ).inspirations
  }

  async uploadInspirationImage(
    inspirationRef: InspirationRef,
    file: File
  ): Promise<UploadImageResponse> {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", "cover-image")
    return await this.post(
      `api/inspirations/${inspirationRef}/upload_image`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
  }

  async uploadInspirationItemImage(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    file: File
  ): Promise<UploadImageResponse> {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", "item-image")
    return await this.post(
      `api/inspirations/${inspirationRef}/items/${inspirationItemId}/upload_image`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
  }

  async uploadInspirationItemAudio(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    file: File
  ): Promise<UploadAudioResponse> {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("type", "item-audio")
    return await this.post(
      `api/inspirations/${inspirationRef}/items/${inspirationItemId}/upload_audio`,
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
      }
    )
  }

  async botGenerateText(
    inspirationRef: InspirationRef,
    inspirationItemId: number | undefined,
    text: string,
    option: InspirationBotGenerateOption,
    where?: InspirationWhere
  ): Promise<string> {
    return await this.post<string>(
      `api/inspirations/${inspirationRef}/generate_text`,
      {
        inspiration_item_id: inspirationItemId,
        original_text: text,
        option: option,
        where: where,
      }
    )
  }

  async createBlockLink(
    inspirationRef: InspirationRef,
    attachment: Attachment
  ) {
    return await this.put<{
      inspiration: InspirationSummary
      items: DetailedInspirationItem[]
    }>(`api/inspirations/${inspirationRef}`, {
      where: InspirationItemAddBlock,
      edit_text: InspirationItemBlockLink,
      day_index: 0, // Obsolete - to be removed later when we changed the api
      attachment: attachment,
    })
  }

  async createBlockGuide(
    inspirationRef: InspirationRef,
    entityRef: InspirationRef
  ) {
    return await this.put<{
      inspiration: InspirationSummary
      items: DetailedInspirationItem[]
    }>(`api/inspirations/${inspirationRef}`, {
      where: InspirationItemAddBlock,
      edit_text: InspirationItemBlockGuide,
      day_index: 0, // Obsolete - to be removed later when we changed the api
      guide: entityRef,
    })
  }

  async createPlaceItemBlockLink(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    attachment: Attachment
  ) {
    return (
      await this.put<{ inspiration_items: DetailedInspirationItem[] }>(
        `api/inspirations/${inspirationRef}/items/${inspirationItemId}`,
        {
          where: InspirationItemUpdateBlock,
          edit_text: InspirationItemBlockLink,
          attachment: attachment,
        }
      )
    ).inspiration_items
  }

  async createPlaceItemBlockAudio(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    audio: PlaceAudio
  ) {
    return (
      await this.put<{ inspiration_items: DetailedInspirationItem[] }>(
        `api/inspirations/${inspirationRef}/items/${inspirationItemId}`,
        {
          where: InspirationItemUpdateBlock,
          edit_text: InspirationItemBlockAudio,
          audio: audio,
        }
      )
    ).inspiration_items
  }

  async insertBlockGuide(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    entityRef: InspirationRef,
    where:
      | typeof InspirationItemAddBlockAfter
      | typeof InspirationItemAddBlockBefore = InspirationItemAddBlockAfter
  ) {
    return (
      await this.put<{ inspiration_items: DetailedInspirationItem[] }>(
        `api/inspirations/${inspirationRef}/items/${inspirationItemId}`,
        {
          where,
          edit_text: InspirationItemBlockGuide,
          guide: entityRef,
        }
      )
    ).inspiration_items
  }

  async insertItemBlockLink(
    inspirationRef: InspirationRef,
    inspirationItemId: number,
    attachment: Attachment,
    where:
      | typeof InspirationItemAddBlockAfter
      | typeof InspirationItemAddBlockBefore = InspirationItemAddBlockAfter
  ) {
    return (
      await this.put<{ inspiration_items: DetailedInspirationItem[] }>(
        `api/inspirations/${inspirationRef}/items/${inspirationItemId}`,
        {
          where,
          edit_text: InspirationItemBlockLink,
          attachment: attachment,
        }
      )
    ).inspiration_items
  }

  async setInspirationVisibility(
    inspirationRef: InspirationRef,
    visibility: "featured" | "normal" | "buried"
  ) {
    return await this.post<InspirationSummary>(
      `api/inspirations/${inspirationRef}/visibility/${visibility}`
    )
  }

  async fetchCreatorCommissions(
    page = 1,
    perPage = 10,
    status?: string,
    sortField?: string | null,
    sortDirection?: "asc" | "desc",
    customConfig?: BaseApiRequestConfig
  ) {
    const params = new URLSearchParams()
    params.append("page", page.toString())
    params.append("per_page", perPage.toString())

    if (status && status !== "All") {
      params.append("status", status.toLowerCase())
    }

    if (sortField) {
      params.append("sort_by", sortField)
      params.append("sort_direction", sortDirection || "desc")
    }

    return await this.get<CommissionResponse>(
      `/api/inspirations/creator/commissions?${params.toString()}`,
      customConfig
    )
  }

  async fetchCommissionMetrics() {
    return await this.get<CommissionMetrics>(
      "api/inspirations/creator/commission_metrics"
    )
  }

  async fetchThatchCommissions(
    page = 1,
    perPage = 10,
    status?: string,
    sortField?: string | null,
    sortDirection?: "asc" | "desc",
    customConfig?: BaseApiRequestConfig
  ) {
    const params = new URLSearchParams()
    params.append("page", page.toString())
    params.append("per_page", perPage.toString())

    if (status && status !== "All") {
      params.append("status", status.toLowerCase())
    }

    if (sortField) {
      params.append("sort_by", sortField)
      params.append("sort_direction", sortDirection || "desc")
    }

    return await this.get<CommissionResponse>(
      `/api/inspirations/creator/thatch_booking_commission?${params.toString()}`,
      customConfig
    )
  }

  async fetchThatchCommissionMetrics() {
    return await this.get<CommissionMetrics>(
      "api/inspirations/creator/thatch_booking_commission_metrics"
    )
  }

  async downloadInspirationPdfAsync(
    inspirationRef: InspirationRef,
    mapViewport?: MapViewport | null,
    config?: BaseApiRequestConfig
  ): Promise<{ job_id: string; existing_job: boolean }> {
    const response = await this.get<{
      job_id: string
      existing_job: boolean
    }>(
      `/api/inspirations/${inspirationRef}/pdf${pdfMapViewportQuery(mapViewport)}`,
      config
    )
    return response
  }
}

export function pdfMapViewportQuery(mapViewport?: MapViewport | null): string {
  if (!mapViewport) return ""

  const params = new URLSearchParams({
    map_lat: mapViewport.lat.toString(),
    map_lng: mapViewport.lng.toString(),
    map_zoom: mapViewport.zoom.toString(),
  })
  return `?${params.toString()}`
}

export interface UploadImageResponse {
  url?: string
  width?: number
  height?: number
  error?: string
}

export interface UploadAudioResponse {
  url: string
  type: string
  error?: string
}
